/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  Optional,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  QueryList,
} from '@angular/core';
import {ENTER, SPACE} from '../keyboard/keycodes';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {MATERIAL_COMPATIBILITY_MODE} from '../compatibility/compatibility';
import {MdOptgroup} from './optgroup';
/**
 * Option IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter = 0;
/**
 * Event object emitted by MdOption when selected or deselected.
 */
export class MdOptionSelectionChange {
/**
 * @param {?} source
 * @param {?=} isUserInput
 */
constructor(public source: MdOption,
public isUserInput = false) { }
}

function MdOptionSelectionChange_tsickle_Closure_declarations() {
/** @type {?} */
MdOptionSelectionChange.prototype.source;
/** @type {?} */
MdOptionSelectionChange.prototype.isUserInput;
}

/**
 * Single option inside of a `<md-select>` element.
 */
export class MdOption {
private _selected: boolean = false;
private _active: boolean = false;
private _multiple: boolean = false;
private _disableRipple: boolean = false;
/**
 * Whether the option is disabled.
 */
private _disabled: boolean = false;
private _id: string = `md-option-${_uniqueIdCounter++}`;
/**
 * Whether the wrapping component is in multiple selection mode.
 * @return {?}
 */
get multiple() { return this._multiple; }
/**
 * @param {?} value
 * @return {?}
 */
set multiple(value: boolean) {
    if (value !== this._multiple) {
      this._multiple = value;
      this._changeDetectorRef.markForCheck();
    }
  }
/**
 * The unique ID of the option.
 * @return {?}
 */
get id() { return this._id; }
/**
 * Whether or not the option is currently selected.
 * @return {?}
 */
get selected(): boolean { return this._selected; }
/**
 * The form value of the option.
 */
value: any;
/**
 * Whether the option is disabled.
 * @return {?}
 */
get disabled() { return (this.group && this.group.disabled) || this._disabled; }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: any) { this._disabled = coerceBooleanProperty(value); }
/**
 * Whether ripples for the option are disabled.
 * @return {?}
 */
get disableRipple() { return this._disableRipple; }
/**
 * @param {?} value
 * @return {?}
 */
set disableRipple(value: boolean) {
    this._disableRipple = value;
    this._changeDetectorRef.markForCheck();
  }
/**
 * Event emitted when the option is selected or deselected.
 */
onSelectionChange = new EventEmitter<MdOptionSelectionChange>();
/**
 * @param {?} _element
 * @param {?} _changeDetectorRef
 * @param {?} group
 */
constructor(
private _element: ElementRef,
private _changeDetectorRef: ChangeDetectorRef,
public readonly group: MdOptgroup) {}
/**
 * Whether or not the option is currently active and ready to be selected.
 * An active option displays styles as if it is focused, but the
 * focus is actually retained somewhere else. This comes in handy
 * for components like autocomplete where focus must remain on the input.
 * @return {?}
 */
get active(): boolean {
    return this._active;
  }
/**
 * The displayed value of the option. It is necessary to show the selected option in the
 * select's trigger.
 * @return {?}
 */
get viewValue(): string {
    // TODO(kara): Add input property alternative for node envs.
    return (this._getHostElement().textContent || '').trim();
  }
/**
 * Selects the option.
 * @return {?}
 */
select(): void {
    this._selected = true;
    this._changeDetectorRef.markForCheck();
    this._emitSelectionChangeEvent();
  }
/**
 * Deselects the option.
 * @return {?}
 */
deselect(): void {
    this._selected = false;
    this._changeDetectorRef.markForCheck();
    this._emitSelectionChangeEvent();
  }
/**
 * Sets focus onto this option.
 * @return {?}
 */
focus(): void {
    const /** @type {?} */ element = this._getHostElement();

    if (typeof element.focus === 'function') {
      element.focus();
    }
  }
/**
 * This method sets display styles on the option to make it appear
 * active. This is used by the ActiveDescendantKeyManager so key
 * events will display the proper options as active on arrow key events.
 * @return {?}
 */
setActiveStyles(): void {
    if (!this._active) {
      this._active = true;
      this._changeDetectorRef.markForCheck();
    }
  }
/**
 * This method removes display styles on the option that made it appear
 * active. This is used by the ActiveDescendantKeyManager so key
 * events will display the proper options as active on arrow key events.
 * @return {?}
 */
setInactiveStyles(): void {
    if (this._active) {
      this._active = false;
      this._changeDetectorRef.markForCheck();
    }
  }
/**
 * Gets the label to be used when determining whether the option should be focused.
 * @return {?}
 */
getLabel(): string {
    return this.viewValue;
  }
/**
 * Ensures the option is selected when activated from the keyboard.
 * @param {?} event
 * @return {?}
 */
_handleKeydown(event: KeyboardEvent): void {
    if (event.keyCode === ENTER || event.keyCode === SPACE) {
      this._selectViaInteraction();

      // Prevent the page from scrolling down and form submits.
      event.preventDefault();
    }
  }
/**
 * Selects the option while indicating the selection came from the user. Used to
 * determine if the select's view -> model callback should be invoked.
 * @return {?}
 */
_selectViaInteraction(): void {
    if (!this.disabled) {
      this._selected = this.multiple ? !this._selected : true;
      this._changeDetectorRef.markForCheck();
      this._emitSelectionChangeEvent(true);
    }
  }
/**
 * Returns the correct tabindex for the option depending on disabled state.
 * @return {?}
 */
_getTabIndex(): string {
    return this.disabled ? '-1' : '0';
  }
/**
 * Gets the host DOM element.
 * @return {?}
 */
_getHostElement(): HTMLElement {
    return this._element.nativeElement;
  }
/**
 * Emits the selection change event.
 * @param {?=} isUserInput
 * @return {?}
 */
private _emitSelectionChangeEvent(isUserInput = false): void {
    this.onSelectionChange.emit(new MdOptionSelectionChange(this, isUserInput));
  }
/**
 * Counts the amount of option group labels that precede the specified option.
 * @param {?} optionIndex Index of the option at which to start counting.
 * @param {?} options Flat list of all of the options.
 * @param {?} optionGroups Flat list of all of the option groups.
 * @return {?}
 */
static countGroupLabelsBeforeOption(optionIndex: number, options: QueryList<MdOption>,
    optionGroups: QueryList<MdOptgroup>): number {

    if (optionGroups.length) {
      let /** @type {?} */ optionsArray = options.toArray();
      let /** @type {?} */ groups = optionGroups.toArray();
      let /** @type {?} */ groupCounter = 0;

      for (let /** @type {?} */ i = 0; i < optionIndex + 1; i++) {
        if (optionsArray[i].group && optionsArray[i].group === groups[groupCounter]) {
          groupCounter++;
        }
      }

      return groupCounter;
    }

    return 0;
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-option, mat-option',
  host: {
    'role': 'option',
    '[attr.tabindex]': '_getTabIndex()',
    '[class.mat-selected]': 'selected',
    '[class.mat-option-multiple]': 'multiple',
    '[class.mat-active]': 'active',
    '[id]': 'id',
    '[attr.aria-selected]': 'selected.toString()',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[class.mat-option-disabled]': 'disabled',
    '(click)': '_selectViaInteraction()',
    '(keydown)': '_handleKeydown($event)',
    'class': 'mat-option',
  },
  templateUrl: 'option.html',
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: ChangeDetectorRef, },
{type: MdOptgroup, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'value': [{ type: Input },],
'disabled': [{ type: Input },],
'onSelectionChange': [{ type: Output },],
};
}

function MdOption_tsickle_Closure_declarations() {
/** @type {?} */
MdOption.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdOption.ctorParameters;
/** @type {?} */
MdOption.propDecorators;
/** @type {?} */
MdOption.prototype._selected;
/** @type {?} */
MdOption.prototype._active;
/** @type {?} */
MdOption.prototype._multiple;
/** @type {?} */
MdOption.prototype._disableRipple;
/**
 * Whether the option is disabled.
 * @type {?}
 */
MdOption.prototype._disabled;
/** @type {?} */
MdOption.prototype._id;
/**
 * The form value of the option.
 * @type {?}
 */
MdOption.prototype.value;
/**
 * Event emitted when the option is selected or deselected.
 * @type {?}
 */
MdOption.prototype.onSelectionChange;
/** @type {?} */
MdOption.prototype._element;
/** @type {?} */
MdOption.prototype._changeDetectorRef;
/** @type {?} */
MdOption.prototype.group;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
